import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Banknote } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import DealFormFields, { missingRequired } from "@/components/DealFormFields";

// Add / Edit Deal Details using the active (or the submission's) versioned deal form.
export default function DealDetailsModal({ open, onOpenChange, leadId, onSaved }) {
  const [form, setForm] = useState(null);     // template fields
  const [version, setVersion] = useState(null);
  const [values, setValues] = useState({});
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/leads/${leadId}/deal-details`);
      const d = data.data;
      if (d.has_deal) {
        // Edit existing — use its immutable field snapshot
        setEditing(true);
        setVersion(d.deal.form_version);
        setForm({ name: d.deal.form_name, fields: (d.deal.items || []).map((i) => ({ field_id: i.field_id, label: i.label, field_type: i.field_type })) });
        // need full field defs incl options -> fetch from snapshot via answers; but items lack options.
        // Re-fetch full snapshot fields from active form if same version, else build from items.
        const af = d.active_form;
        if (af && af.version === d.deal.form_version) {
          setForm({ name: af.name, fields: af.fields });
        }
        setValues(d.deal.answers || {});
      } else {
        setEditing(false);
        if (!d.active_form) { toast.error("No active deal form configured"); onOpenChange(false); return; }
        setForm({ name: d.active_form.name, fields: d.active_form.fields });
        setVersion(d.active_form.version);
        const init = {};
        (d.active_form.fields || []).forEach((f) => { if (f.field_type === "date" && f.field_id === "proposal_sent_date") init[f.field_id] = new Date().toISOString().slice(0, 10); });
        setValues(init);
      }
    } catch (e) { toast.error(apiError(e)); onOpenChange(false); }
    finally { setLoading(false); }
  }, [leadId, onOpenChange]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const onChange = (id, v) => setValues((s) => ({ ...s, [id]: v }));

  const submit = async () => {
    const miss = missingRequired(form?.fields || [], values);
    if (miss.length) return toast.error(`Please fill: ${miss.join(", ")}`);
    setSaving(true);
    try {
      await api.post(`/leads/${leadId}/deal-details`, { answers: values });
      toast.success(editing ? "Deal details updated" : "Deal details saved");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto border-sky bg-white sm:max-w-lg" data-testid="deal-details-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-sora text-navy"><Banknote className="h-5 w-5 text-emerald-600" />{editing ? "Edit Deal Details" : "Add Deal Details"}</DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-xs text-ink/60">The commercial proposal — what you pitched, for how much, and when it can close.{version ? ` Form v${version}.` : ""}</DialogDescription>
        {loading || !form ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-ink/50"><Loader2 className="h-4 w-4 animate-spin" />Loading form…</div>
        ) : (
          <DealFormFields fields={form.fields} values={values} onChange={onChange} idPrefix="deal" />
        )}
        <div className="mt-3 flex justify-end gap-2">
          <button onClick={() => onOpenChange(false)} data-testid="deal-cancel" className="rounded-lg border border-sky px-4 py-2 text-sm font-medium text-ink/70 hover:bg-mist">Cancel</button>
          <button onClick={submit} disabled={saving || loading} data-testid="deal-save" className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}{editing ? "Save changes" : "Save deal details"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
